import { ReactNode } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, Divider, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import type { CheckOperationSnapshot } from '../../../../../hooks/Marketing';
import ColoredDialogTitle from '../creation/shared/ColoredDialogTitle';
import ResumeSectionLabel from './ResumeSectionLabel';
import { CHANNEL_BY_ID, CHANNEL_LABELS } from '../channels';

interface VerifyRecapDialogProps {
  open: boolean;
  snapshot: CheckOperationSnapshot | null;
  validating: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, py: 0.25 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>{label}</Typography>
      <Typography variant="body2" fontWeight={500}>{children}</Typography>
    </Box>
  );
}

function VerifyRecapDialog({ open, snapshot, validating, onClose, onConfirm }: VerifyRecapDialogProps) {
  const comm = snapshot?.communication;
  const channel = comm?.channelId != null ? CHANNEL_BY_ID[comm.channelId] : undefined;

  return (
    <Dialog open={open} onClose={validating ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <ColoredDialogTitle onClose={onClose}>Récapitulatif de la campagne</ColoredDialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, color: 'success.main' }}>
          <CheckCircleOutlineIcon fontSize="small" />
          <Typography variant="body2" fontWeight={600}>La vérification de l'opération est valide.</Typography>
        </Box>

        {snapshot ? (
          <>
            <ResumeSectionLabel>Campagne</ResumeSectionLabel>
            <Row label="Titre">{comm?.title || '—'}</Row>
            {comm?.description && <Row label="Description">{comm.description}</Row>}
            <Row label="Canal">{channel ? CHANNEL_LABELS[channel] : '—'}</Row>
            {comm?.notSendable
              ? <Row label="Envoi">Non envoyable</Row>
              : <Row label="Période d'envoi">{formatDate(comm?.sendStart ?? null)} → {formatDate(comm?.sendEnd ?? null)}</Row>}
            {(comm?.targetTitle || comm?.targetDescription) && <Row label="Ciblage">{comm.targetTitle || comm.targetDescription}</Row>}
            <Row label="Création">{comm?.creationPayloadPresent ? 'Renseignée' : 'Aucune'}</Row>

            <Divider sx={{ my: 1.5 }} />
            <ResumeSectionLabel>Offre(s) ({snapshot.offers.length})</ResumeSectionLabel>
            {snapshot.offers.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Aucune offre</Typography>
            ) : (
              snapshot.offers.map(offer => (
                <Box key={offer.offerInputId} sx={{ mb: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {offer.title || 'Offre'}{offer.offerNumber ? ` · ${offer.offerNumber}` : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Du {formatDate(offer.startValidityDate)} au {formatDate(offer.endValidityDate)}
                    {offer.maxEditCount ? ` · max ${offer.maxEditCount} éd.` : ''}
                    {offer.maxUseCount ? ` · max ${offer.maxUseCount} util.` : ''}
                  </Typography>
                </Box>
              ))
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">Aucun récapitulatif renvoyé par la vérification.</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={validating} sx={{ color: 'text.secondary', textTransform: 'none' }}>
          Retour
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={validating}
          startIcon={validating ? <CircularProgress size={14} color="inherit" /> : undefined}
          sx={{ backgroundColor: 'primary.main', textTransform: 'none', fontWeight: 600, '&:hover': { backgroundColor: 'primary.main', opacity: 0.9 } }}
        >
          Valider la campagne
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default VerifyRecapDialog;
